import { renderCell, renderHeader } from '../components/table';
import { useMemo, useContext } from 'react';
import { Flex, Box, Center, Spinner } from '@chakra-ui/react';
import { Wrapper } from '../components/Wrapper';
import { BreakdownBy } from '../components/breakdown-by';
import { applyGroup } from '@/store/atsReports/jobs';
import { useDispatch, useSelector } from 'react-redux';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import {
  selectJobsTable,
  selectJobsTableLoading,
  selectJobsReportsGroup,
} from '@/store/atsReports/jobs/selectors';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';

interface Option {
  label: string;
  id: number | string | null;
}

interface GroupOption extends Option {
  key: string;
}

interface TableViewProps {
  groupBy1?: GroupOption;
  groupBy2?: GroupOption;
  customData?: any[];
  isReadOnly?: boolean;
  isPrintView?: boolean;
  customBreakdownChange?: (key: string, option: Option) => void;
  renderTitle?: () => React.ReactNode;
  onDrillDownOpen?: (data: any) => void;
  isStaticReport?: boolean;
  onSortChange?: (sortState: string) => void;
  isEditMode?: boolean;
  tableColStyles?: any;
  onTableColDataChange?: (tableColData: {
    order: string[];
    visibility: Record<string, boolean>;
  }) => void;
  initialTableColData?: {
    order: string[];
    visibility: Record<string, boolean>;
  };
  skipLocalStorage?: boolean;
  feReportsV3?: boolean;
  isFullScreen?: boolean;
  isFilterApplied?: boolean;
}

const GROUP_BY_OPTIONS: GroupOption[] = [
  { id: null, label: 'Time', key: 'dt' },
  { id: 'primary_recruiter', label: 'Primary recruiter', key: 'g1.name' },
  { id: 'primary_hm', label: 'Primary hiring manager', key: 'g1.name' },
  { id: 'root_department', label: 'Department', key: 'g1.name' },
  { id: 'job_location', label: 'Location (on job)', key: 'g1.name' },
];

const BREAKDOWN_BY_OPTIONS: Option[] = [
  { id: null, label: 'None' },
  { id: 'job_name', label: 'Job name' },
  { id: 'job_status', label: 'Job status' },
  { id: 'primary_recruiter', label: 'Primary recruiter' },
  { id: 'primary_hm', label: 'Primary hiring manager' },
  { id: 'department', label: 'Department' },
  { id: 'job_location', label: 'Location (on job)' },
];

export const TableView: React.FC<TableViewProps> = ({
  groupBy1,
  groupBy2,
  isPrintView = false,
  customData,
  isReadOnly = false,
  customBreakdownChange,
  renderTitle,
  onDrillDownOpen = () => {},
  isStaticReport = false,
  onSortChange = (_sortState) => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  feReportsV3 = false,
  isFullScreen = false,
  isFilterApplied = false,
}) => {
  const dispatch = useDispatch();
  const { consumer } = useContext(ReportSelectionContext);
  const reduxIsLoading = useSelector(selectJobsTableLoading);
  const reduxJobsTable = useSelector(selectJobsTable);
  const reduxGroup = useSelector(selectJobsReportsGroup);

  const isTableLoading = feReportsV3 ? false : reduxIsLoading;
  const jobsTable = feReportsV3 ? [] : reduxJobsTable;
  const group = feReportsV3 ? null : reduxGroup;

  const dataToUse = customData ?? jobsTable;
  const isDataEmpty = isReportTableEmpty(dataToUse, 'opened_jobs');

  const { handleDrillDown } = useDrillDownHandler(true, onDrillDownOpen);
  const getGroupValue = (value: any, fallbackValue: any) =>
    feReportsV3 ? value : value || fallbackValue;

  const drilldownGroup = {
    group_by_1: getGroupValue(groupBy1, group?.group_by_1),
    group_by_2: getGroupValue(groupBy2, group?.group_by_2),
  };

  const createDrillDownCell = (accessor: string, title: string) =>
    renderCell({
      title,
      accessor,
      width: 200,
      sort: true,
      render: (_, row) => (
        <Flex
          _hover={{ textDecoration: 'underline' }}
          cursor="pointer"
          onClick={() =>
            handleDrillDown(
              row,
              title,
              false,
              drilldownGroup,
              null,
              row?.[accessor]
            )
          }
        >
          {row?.[accessor]}
        </Flex>
      ),
      renderFooter: (info) => {
        const row = info?.rows?.[0]?.original;
        return (
          <Flex
            _hover={{ textDecoration: 'underline' }}
            cursor="pointer"
            onClick={() =>
              handleDrillDown(
                row,
                title,
                true,
                drilldownGroup,
                null,
                row?.[`${accessor}_total`]
              )
            }
          >
            <KText size="sm-semi-bold">{row?.[`${accessor}_total`]}</KText>
          </Flex>
        );
      },
    });

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- pre-existing; unrelated to flag removal
  const columns = useMemo(() => {
    const header = feReportsV3
      ? groupBy1 || GROUP_BY_OPTIONS[0]
      : groupBy1 || group?.group_by_1 || GROUP_BY_OPTIONS[0];
    return [
      renderHeader({
        accessor: 'header',
        title: header?.label ?? 'Name',
        width: 250,
        expand: false,
        sort: true,
        isReadOnly,
        groupBy: group?.group_by_1?.id,
        breakDownBy: group?.group_by_2?.id,
        columnConfig: false,
        renderFooter: () => <KText size="sm-semi-bold">Total</KText>,
      }),
      createDrillDownCell('opened_jobs', 'Jobs opened'),
      createDrillDownCell('closed_jobs', 'Jobs closed'),
      createDrillDownCell('closed_without_hires', 'Jobs closed without hires'),
      createDrillDownCell('target_breached_jobs', 'Target date breached jobs'),
    ];
  }, [groupBy1, groupBy2, feReportsV3, group]); // eslint-disable-line

  const handleBreakdownChange = (key: string, option: Option) => {
    if (customBreakdownChange) {
      customBreakdownChange(key, option);
      return;
    }

    if (key === 'group_by_1') {
      dispatch(applyGroup({ key: 'group_by_1', value: option }));
    } else if (key === 'group_by_2') {
      dispatch(applyGroup({ key: 'group_by_2', value: option }));
    }
  };

  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: GROUP_BY_OPTIONS,
      selected: feReportsV3
        ? groupBy1 || GROUP_BY_OPTIONS[0]
        : groupBy1 || group?.group_by_1 || GROUP_BY_OPTIONS[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: BREAKDOWN_BY_OPTIONS,
      selected: feReportsV3
        ? groupBy2 || BREAKDOWN_BY_OPTIONS[0]
        : groupBy2 || group?.group_by_2 || BREAKDOWN_BY_OPTIONS[0],
    },
  ];

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  if (isFilterApplied) {
    return (
      <EmptyReportChart
        text="Filters not applicable"
        subText="The applied filters are not applicable for this block"
      />
    );
  }

  return (
    <Wrapper role="group" isReadOnly={isReadOnly || isStaticReport}>
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle ? (
            renderTitle()
          ) : (
            <Box flex="1">
              <TitleWithTooltip
                label="Jobs breakdown"
                metricDefinition="Comprehensive breakdown of the jobs data"
              />
            </Box>
          )}
          <Flex
            gap="8px"
            mr={
              isPrintView
                ? '0'
                : isDataEmpty
                  ? '35px'
                  : isEditMode
                    ? '55px'
                    : '35px'
            }
            mb="12px"
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  handleBreakdownChange(key, option)
                }
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isTableLoading ? (
        <Center minHeight="200px">
          <Spinner color="primary-800" size="sm" />
        </Center>
      ) : (
        <EmptyDataWrapper data={dataToUse} field="opened_jobs">
          <Table
            tableId="jobs-table"
            skipLocalStorage={skipLocalStorage}
            defaultSortBy={[{ id: 'header', desc: false }]}
            isColumnLevelSorting
            enableColumnConfig={isEditMode}
            tableColStyles={tableColStyles}
            columns={columns}
            data={dataToUse}
            enableFooter={true}
            layoutScrollContainerStyles="c-data-table wide-content table-container"
            enableExpanded={true}
            layoutStyles={{
              borderBottom: 'none',
            }}
            getTableState={handleSortChange}
            onTableColDataChange={onTableColDataChange}
            initialTableColData={initialTableColData}
          />
        </EmptyDataWrapper>
      )}
    </Wrapper>
  );
};
